/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { CryptoProviderFactory } from "../device/crypto";
import { Log } from "../logging/Log";

const TAG: string = "checksumUtils";

/**
 * Compute a SHA-256 checksum of the given data.
 *
 * The data is serialised with `JSON.stringify`, encoded to UTF-8, and hashed
 * using the browser-native `crypto.subtle.digest` API — no third-party
 * libraries are used.
 *
 * @param data - The data to hash. Must be the same object that will be stored
 *               in the record's `data` field so that read-time verification
 *               produces an identical digest.
 * @returns A lowercase hex-encoded SHA-256 digest string
 * @throws Re-throws any error from JSON.stringify or crypto.subtle.digest after logging
 */
export async function computeChecksum(data: unknown): Promise<string> {
  try {
    const cryptoProvider = CryptoProviderFactory.getProvider();

    return await cryptoProvider.computeSha256(JSON.stringify(data));
  } catch (error) {
    Log.e(TAG, "computeChecksum()", error);

    throw error;
  }
}

/**
 * Verify that a SHA-256 checksum matches the given data.
 *
 * Recomputes the checksum of `data` and compares it with `storedChecksum`.
 * A mismatch indicates that the data has been corrupted or tampered with
 * since it was written.
 *
 * @param data - The data to verify (same serialisation rules as {@link computeChecksum})
 * @param storedChecksum - The hex-encoded checksum that was stored alongside the data
 * @returns true if the recomputed checksum matches storedChecksum; false otherwise
 * @throws Re-throws any error from {@link computeChecksum} after logging
 */
export async function verifyChecksum(
  data: unknown,
  storedChecksum: string
): Promise<boolean> {
  try {
    const computed: string = await computeChecksum(data);
    return computed === storedChecksum;
  } catch (error: unknown) {
    Log.e(TAG, "verifyChecksum()", error);
    throw error;
  }
}
